import React from 'react'
import { InferGetServerSidePropsType } from 'next'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import { useGetHrefContext } from '@scentregroup/shared/context'
import {
  fetchNationalStaticData,
  NationalStaticData,
} from '@scentregroup/shared/helpers/fetch-header-static-data'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../types/next'
import { makeApolloClient } from '../../../lib/apolloClient'
import {
  ContentDetail,
  getContentDetail,
} from '../../../lib/get-content-detail'
import { getDefaultNationalMetaData } from '../../../../../src/shared/helpers/get-default-national-metadata'
import { StoryContentDetail } from '../../../components/page-templates/content-detail/story'
import { META_FALLBACK_IMAGE } from '../../../../../src/shared/constants'
import { shouldRedirectExpiredNationalContent } from '../../../lib/redirect-expired-content'
import { getNewsArticleStructuredDataFromContent } from '../../../lib/schema/get-news-article-structured-data-from-content'
import { shouldRedirectForNationalContentWithSlugMismatch } from '../../../lib/redirects'

export interface INationalStoryPageProps {
  country: ReturnType<typeof localeToCountry>
  topStripProps: ITopStripProps | null
  story: ContentDetail['story']
  headerStaticData: NationalStaticData
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext<{ id: string; slug: string }>
): Promise<GetServerSidePropsResult<INationalStoryPageProps>> => {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)
  const id = ctx.params?.id
  if (!id) {
    return {
      notFound: true,
    }
  }

  const apollo = makeApolloClient()

  const [headerStaticData, story, topStripProps] = await Promise.all([
    fetchNationalStaticData(apollo, { country }),
    getContentDetail(apollo, 'story', { id }),
    getTopStripProps(apollo, country),
  ])

  if (!story) {
    return {
      notFound: true,
    }
  }

  const redirect =
    shouldRedirectExpiredNationalContent(story.hideAt, '/', country) ||
    shouldRedirectForNationalContentWithSlugMismatch(
      'story',
      ctx?.params?.slug,
      story
    )

  if (redirect) {
    return redirect
  }

  return {
    props: {
      country,
      topStripProps,
      headerStaticData,
      story,
    },
  }
}

const StoryDetailPage: React.FC<
  React.PropsWithChildren<
    InferGetServerSidePropsType<typeof getServerSideProps>
  >
> = props => {
  const { country, topStripProps, story, headerStaticData } = props

  const metaTitle = story.title
  const metaDescription = getDefaultNationalMetaData({
    country,
    type: 'default',
  }).description
  const metaCanonical = completelyCanonicalUrl(useGetHrefContext()())

  const metaData = {
    title: metaTitle,
    description: metaDescription,
    canonical: metaCanonical,
    openGraph: {
      url: metaCanonical,
      title: metaTitle,
      description: metaDescription,
      image: story.heroImageSet?.master?.url || META_FALLBACK_IMAGE,
    },
  }

  const schema = story.heroImageSet?.master
    ? getNewsArticleStructuredDataFromContent(story, metaCanonical)
    : undefined

  return (
    <>
      <SGLinkedData item={schema} />
      <Layout
        country={country}
        topStrip={topStripProps}
        centre={undefined}
        header={{
          variant: 'national',
          data: headerStaticData,
        }}
        metaData={{ ...metaData }}
        breadcrumbs={[
          { label: 'Home', url: '/' },
          { label: 'The Edit', url: '/the-edit' },
          { label: metaData.title },
        ]}
      >
        <StoryContentDetail content={story} />
      </Layout>
    </>
  )
}

export default StoryDetailPage
